import SlideGallery from '@/components/app/templates/slide-gallery/SlideGallery';
import DownloadItemButton from '@/components/app/utils/download-item-button/DownloadItemButton';
import GslidesIcon from '@/components/icons/gslides';
import PptIcon from '@/components/icons/powerpoint';
import { HeartFilled, HeartOutlined } from '@ant-design/icons';
import {
   Avatar,
   Breadcrumb,
   Button,
   Card,
   Col,
   Descriptions,
   Divider,
   Flex,
   Row,
   Skeleton,
   Space,
   Tag,
   Typography,
} from 'antd';
import axios from 'axios';
import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';
import useSWR from 'swr';
import CustomDesignWidget from '../CustomDesignWidget';
import TemplatesCard from '../template-card';
import styles from './TemplateView.module.css';

const { Title, Text } = Typography;

export default function TemplateView({ slug, id }) {
   const [descriptionExpanded, setDescriptionExpanded] = useState(false);
   const [likeLoading, setLikeLoading] = useState(false);
   const template = useSWR(slug || id ? `/api/templates/${slug}` : null, async (url) => {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Failed to fetch template: ${res.status}`);
      return res.json();
   });

   const relatedTemplatesString = template?.data?.related ? template?.data?.related.join(',') : '';
   const relatedTemplates = useSWR(
      relatedTemplatesString ? `/api/templates?include_ids=${relatedTemplatesString}&category=884` : null,
      async (url) => {
         const res = await fetch(url);
         if (!res.ok) throw new Error(`Failed to fetch related templates: ${res.status}`);
         const data = await res.json();
         return data?.data || [];
      }
   );

   async function handleIsLiked() {
      setLikeLoading(true);
      await axios.post('/api/user/favourites', { item_id: template.data.id }).then(() => {
         template.mutate().then(() => setLikeLoading(false));
      });
   }

   if (template.isLoading) {
      return (
         <Flex gap={32}>
            <Flex vertical gap={32} className="template-view" style={{ width: '70%' }}>
               <Skeleton.Image active style={{ width: '100%', height: 455 }} />
            </Flex>
            <Flex vertical gap={32} className="template-details" style={{ width: '30%' }}>
               <Skeleton.Input active size="small" block style={{ width: '90%' }} />
               <Flex vertical gap={16}>
                  <Skeleton.Input active size="small" block style={{ height: 14, width: '90%' }} />
                  <Skeleton.Input active size="small" block style={{ height: 14, width: '90%' }} />
                  <Skeleton.Input active size="small" style={{ height: 14, width: '60%' }} />
               </Flex>
               <Skeleton.Image active style={{ width: '100%', height: 45 }} />
            </Flex>
         </Flex>
      );
   }

   if (template.error) {
      return <p>Error: {template.error.message}</p>;
   }
   if (template.data) {
      return (
         <Flex vertical gap={32}>
            <Breadcrumb
               separator=">"
               style={{
                  fontSize: '14px',
                  fontWeight: '400',
               }}
            >
               <Breadcrumb.Item>
                  <Link href="/dashboard">Home</Link>
               </Breadcrumb.Item>
               <Breadcrumb.Item>
                  <Link href="/templates">Templates</Link>
               </Breadcrumb.Item>
               <Breadcrumb.Item>{template.data.name}</Breadcrumb.Item>
            </Breadcrumb>
            <Flex gap={32} className={styles.container}>
               <Flex vertical gap={32} className={styles.templateView}>
                  {Array.isArray(template.data.slides_gallery) ? (
                     <SlideGallery slides={template.data.slides_gallery?.map((slide) => slide.url)} />
                  ) : Array.isArray(template.data.gallery) ? (
                     <SlideGallery
                        slides={template.data.gallery?.map((slide) => slide.large)}
                        className={styles.featured_image}
                     />
                  ) : (
                     <Card variant="outlined" styles={{ body: { padding: 0, overflow: 'hidden', display: 'flex' } }}>
                        <Image
                           src={template.data.featured_image}
                           alt={template.name}
                           width={1000}
                           height={850}
                           className={styles.featured_image}
                        />
                     </Card>
                  )}
               </Flex>
               <Flex vertical gap={32} className={styles.templateDetails}>
                  <Flex vertical gap={32}>
                     <Row key="top action bar" justify="space-between" align="middle">
                        {template.data.price > 0 && (
                           <Tag
                              color="gold"
                              style={{
                                 fontSize: '14px',
                                 fontWeight: '400',
                                 width: 'fit-content',
                                 padding: '0 8px',
                              }}
                           >
                              Premium
                           </Tag>
                        )}

                        <Button
                           icon={template.data.is_liked ? <HeartFilled /> : <HeartOutlined />}
                           shape="circle"
                           type={template.data.is_liked ? 'primary' : 'default'}
                           onClick={handleIsLiked}
                           loading={likeLoading}
                        />
                     </Row>

                     <Title level={1}>{template.data.name}</Title>
                     <Flex gap={32}>
                        <Avatar src={template.data.avatar} alt={template.data.author_information?.name} />
                        <Flex vertical gap={16}>
                           <Text>by {template.data.author_information?.name}</Text>
                           <Text style={{ fontSize: 10 }}>{template.data.author_information?.username}</Text>
                        </Flex>
                     </Flex>
                  </Flex>
                  <DownloadItemButton
                     itemId={template.data.id}
                     itemType="template"
                     itemPremium={template.data.price > 0}
                     item={template.data}
                  />

                  <CustomDesignWidget />

                  <Flex vertical gap={16}>
                     <Descriptions size="small" column={1} variant="outlined" title="Details">
                        <Descriptions.Item label="Combetible with">
                           <Tag
                              icon={
                                 template.data.software === 'powerpoint' ? (
                                    <PptIcon />
                                 ) : template.data.software === 'google-slides' ? (
                                    <GslidesIcon />
                                 ) : null
                              }
                              color={
                                 template.data.software === 'powerpoint'
                                    ? 'volcano'
                                    : template.data.software === 'google-slides'
                                    ? 'gold'
                                    : 'default'
                              }
                              style={{
                                 fontSize: '16px',
                                 fontWeight: '400',
                                 width: 'fit-content',
                                 padding: '0 8px',
                              }}
                           >
                              {' '}
                              {template.data.software === 'powerpoint'
                                 ? 'PowerPoint'
                                 : template.data.software === 'google-slides'
                                 ? 'Google Slides'
                                 : null}
                           </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Last updated">{template.data.last_update}</Descriptions.Item>
                        <Descriptions.Item label="Published">{template.data.published}</Descriptions.Item>
                     </Descriptions>

                     <Divider
                        orientation="left"
                        style={{
                           width: '100%',
                           margin: '32px 0',
                           fontSize: '14px',
                           fontWeight: '400',
                           color: 'rgba(0, 0, 0, 0.5)',
                        }}
                     >
                        Tags
                     </Divider>
                     <Space wrap>
                        {template.data.tags.map((tag, i) => (
                           <Tag
                              key={i}
                              color="default"
                              style={{
                                 fontSize: '14px',
                                 fontWeight: '400',
                                 width: 'fit-content',
                                 padding: '0 8px',
                              }}
                           >
                              {tag.name}
                           </Tag>
                        ))}
                     </Space>
                  </Flex>
               </Flex>
            </Flex>
            <Divider />
            <Flex
               gap={32}
               align="center"
               onClick={() => setDescriptionExpanded(!descriptionExpanded)}
               style={{ cursor: 'pointer' }}
            >
               <Title level={2}>Description</Title>
               <Button
                  type="text"
                  shape="circle"
                  onClick={() => setDescriptionExpanded(!descriptionExpanded)}
                  icon={<i className={descriptionExpanded ? 'fi fi-rr-angle-up' : 'fi fi-rr-angle-down'} />}
               />
            </Flex>

            {descriptionExpanded && (
               <div dangerouslySetInnerHTML={{ __html: template.data.description }} className={styles.description} />
            )}

            <Divider />

            <Title level={2}>More like this</Title>

            {relatedTemplates.isLoading && (
               <Row gutter={[16, 16]}>
                  {template.data.related.map((t) => (
                     <Col key={t} xs={12} sm={8} md={6} lg={6}>
                        <Card variant="outlined" styles={{ body: { padding: 8, overflow: 'hidden' } }}>
                           <Flex vertical gap={16}>
                              <Skeleton.Image active style={{ width: '100%', height: 155 }} />
                              <Skeleton.Input active size="small" block style={{ height: 14, width: '90%' }} />
                              <Skeleton.Input active size="small" style={{ height: 14, width: '60%' }} />
                           </Flex>
                        </Card>
                     </Col>
                  ))}
               </Row>
            )}

            {relatedTemplates?.data && (
               <Row gutter={[16, 16]}>
                  {relatedTemplates?.data?.map((t) => (
                     <Col key={t.id} xs={12} sm={8} md={6} lg={6}>
                        <TemplatesCard template={t} />
                     </Col>
                  ))}
               </Row>
            )}
         </Flex>
      );
   }
   return null;
}
